import React, { forwardRef } from 'react';
import classNames from 'classnames';

import { Comp, useId } from '@redsift/design-system';

import { ChartSize, ColorTheme, TooltipVariant } from '../../types';
import { ScatterPlotLegendVariant, ScatterPlotProps, ScatterPlotVariant } from './types';
import { LoadingScatterPlot } from './LoadingScatterPlot';
import { EmptyScatterPlot } from './EmptyScatterPlot';
import { RenderedScatterPlot } from './RenderedScatterPlot';
import { AxisVariant } from '../Axis';

const COMPONENT_NAME = 'ScatterPlot';
const CLASSNAME = 'redsift-scatterplot';

/**
 * ScatterPlot displays relationships between two numeric variables.
 * Built on D3.js with interactive dots and brush selection.
 *
 * Supports zooming, brushing for multi-select, and dot clustering.
 *
 * Data format: `CoordinatesCategoryData` `[{ x: number, y: number, category?: string }]`
 *
 * @example
 * // Basic scatter plot
 * const data = [
 *   { x: 10, y: 20, label: 'Point A' },
 *   { x: 25, y: 15, label: 'Point B' },
 *   { x: 30, y: 35, label: 'Point C' },
 * ];
 * <ScatterPlot data={data} size="medium" />
 *
 * @example
 * // With tooltips and click handler
 * <ScatterPlot
 *   data={correlationData}
 *   tooltipVariant="label"
 *   onDotClick={(d) => showDetails(d)}
 * />
 *
 * @example
 * // Brush selection (Shift+drag to select multiple)
 * <ScatterPlot
 *   data={analysisData}
 *   onBrush={(selected) => highlightPoints(selected)}
 *   onBrushEnd={(selected) => filterData(selected)}
 *   hideControlKeyPanel={false}
 * />
 *
 * @example
 * // Colored by category with legend
 * const categorizedData = [
 *   { x: 10, y: 20, category: 'Group A' },
 *   { x: 15, y: 25, category: 'Group B' },
 *   { x: 20, y: 15, category: 'Group A' },
 * ];
 * <ScatterPlot
 *   data={categorizedData}
 *   colorTheme="categorical"
 *   legendVariant="externalLabel"
 * />
 */

export const ScatterPlot: Comp<ScatterPlotProps, HTMLDivElement> = forwardRef((props, ref) => {
  const {
    className,
    colorTheme = ColorTheme.default,
    data: propsData,
    dotRole,
    emptyComponent,
    hideControlKeyPanel = false,
    id: propsId,
    isDotSelected,
    labelDecorator,
    legendDecorator,
    legendVariant: propsLegendVariant,
    localeText = { emptyChartText: 'No Data' },
    onBrush,
    onBrushEnd,
    onDotClick,
    size = ChartSize.medium,
    sortingMethod = 'desc-value',
    tooltipDecorator,
    tooltipVariant = TooltipVariant.none,
    variant = ScatterPlotVariant.default,
    xAxisPlacement = 'bottom',
    xAxisVariant = AxisVariant.default,
    yAxisPlacement = 'left',
    yAxisVariant = AxisVariant.default,
    ...forwardedProps
  } = props;
  const [_id] = useId();
  const id = propsId ?? _id;

  const legendVariant = propsLegendVariant ?? ScatterPlotLegendVariant.externalLabel;

  if (propsData === undefined || propsData === null) {
    return <LoadingScatterPlot id={id} {...forwardedProps} ref={ref} />;
  }

  if (propsData.length === 0) {
    return (
      <EmptyScatterPlot
        data={propsData}
        emptyComponent={emptyComponent}
        hideControlKeyPanel={hideControlKeyPanel}
        localeText={localeText}
        size={size}
        sortingMethod={sortingMethod}
        xAxisPlacement={xAxisPlacement}
        xAxisVariant={xAxisVariant}
        yAxisPlacement={yAxisPlacement}
        yAxisVariant={yAxisVariant}
        {...forwardedProps}
        ref={ref}
      />
    );
  }

  return (
    <RenderedScatterPlot
      className={classNames(ScatterPlot.className, className)}
      colorTheme={colorTheme}
      data={propsData}
      dotRole={dotRole}
      hideControlKeyPanel={hideControlKeyPanel}
      id={id}
      isDotSelected={isDotSelected}
      labelDecorator={labelDecorator}
      legendDecorator={legendDecorator}
      legendVariant={legendVariant}
      onBrush={onBrush}
      onBrushEnd={onBrushEnd}
      onDotClick={onDotClick}
      size={size}
      sortingMethod={sortingMethod}
      tooltipDecorator={tooltipDecorator}
      tooltipVariant={tooltipVariant}
      variant={variant}
      xAxisPlacement={xAxisPlacement}
      xAxisVariant={xAxisVariant}
      yAxisPlacement={yAxisPlacement}
      yAxisVariant={yAxisVariant}
      {...forwardedProps}
      ref={ref}
    />
  );
});
ScatterPlot.className = CLASSNAME;
ScatterPlot.displayName = COMPONENT_NAME;
